import type { Role } from '@/types';

/** Internal Users access (story 101637): Super Admin + Admin only. Mirrors PAYMENTS_ROLES. (D15) */
export const ADMIN_USERS_ROLES: Role[] = ['super_admin', 'admin'];
/** Human page name for the access-denied toast (ADR-0014). */
export const ADMIN_USERS_PAGE_NAME = 'Internal Users';
/** Glossary display labels for the internal roles (CONTEXT.md). "Operational Associate" and
 *  "Field Installer" are the canonical names as of the 2026-07-24 BA decision on story 101637 —
 *  customer support and logistics were merged into one role. Never the retired names
 *  "Customer Support + Logistics"/"Field Technician". (D1) */
export const ROLE_LABEL: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  operational_associate: 'Operational Associate',
  field_installer: 'Field Installer',
  dealer: 'Dealer',
};

/** The three roles an Admin/Super Admin may assign when creating a user (D1). `super_admin` and
 *  `dealer` are never creatable here. Labels come from ROLE_LABEL (glossary strings). */
export const CREATABLE_ROLES = ['admin', 'operational_associate', 'field_installer'] as const;
export type CreatableRole = (typeof CREATABLE_ROLES)[number];
export const CREATABLE_ROLE_OPTIONS = CREATABLE_ROLES.map((value) => ({
  value,
  label: ROLE_LABEL[value],
}));

/** Typed dependency-conflict codes (ADR-0008) — a 409 when the user has active work. Disable **and**
 *  Delete both raise these; branch on the constant, never the raw string. (D5) */
export const USER_DEPENDENCY_CODES = {
  installationRequest: 'USER_HAS_ACTIVE_INSTALLATION_REQUEST',
  supportTicket: 'USER_HAS_ASSIGNED_SUPPORT_TICKET',
} as const;

export type UserDependencyCode = (typeof USER_DEPENDENCY_CODES)[keyof typeof USER_DEPENDENCY_CODES];

/** True when an ApiError code is one of the two dependency-block codes. */
export function isUserDependencyCode(code: string | undefined): code is UserDependencyCode {
  return (
    code === USER_DEPENDENCY_CODES.installationRequest ||
    code === USER_DEPENDENCY_CODES.supportTicket
  );
}

/** Block-dialog copy for a dependency-blocked Disable/Delete (D10, frames 8152-*). Title-case title
 *  per action; body names the offending dependency. */
export function userBlockCopy(
  action: 'disabled' | 'deleted',
  code: UserDependencyCode,
): { title: string; body: string } {
  const dependency =
    code === USER_DEPENDENCY_CODES.installationRequest
      ? 'an active installation request'
      : 'a support ticket assigned';
  return {
    title: action === 'deleted' ? 'Cannot Be Deleted' : 'Cannot Be Disabled',
    body: `User with ${dependency} cannot be ${action}.`,
  };
}
